import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { useFormik } from "formik";
import React, { useEffect, useRef, useState } from "react";
import api from "../../axiosInstance";
import { toast } from "react-toastify";
import {
  BUTTON_STYLES,
  FORM_STYLES,
  MODAL_STYLES,
  TEXT_FIELD_STYLES,
} from "../../styles/componentStyles";
import AcademicsValidation from "../Validations/AcademicsValidation";

const { BlogValidationSchema } = AcademicsValidation;

const emptyFaq = () => ({ q: "", a: "" });

// Blog.faqs is now topic-grouped: [{ topic, items: [{q,a}] }]. A blog saved
// before this existed still holds the old flat [{q,a}] shape -- normalized
// here into one implicit, untitled group so both BlogModal and the backend
// payload only ever deal with the new shape. Nothing is rewritten in the
// DB until the admin next saves this blog.
const normalizeFaqs = (raw) => {
  if (!Array.isArray(raw) || raw.length === 0) return [];
  if (raw[0] && typeof raw[0].q === "string") {
    return [{ topic: null, items: raw }];
  }
  return raw;
};

// Slug is internal-only (URL/API routing) -- the public blog page never
// shows it, so the admin never types one. Derived from Title on create;
// an existing blog's slug is preserved as-is on edit so its public URL
// never changes just because the title was tweaked.
const slugify = (title) =>
  (title || "")
    .toString()
    .trim()
    .toLowerCase()
    .replace(/['"]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

function BlogModal({ open, onClose, blogEdit, onSuccess, categoriesVersion = 0 }) {
  const formik = useFormik({
    initialValues: {
      title: blogEdit?.title || "",
      category: blogEdit?.category || "",
      description: blogEdit?.description || "",
      readTime: blogEdit?.readTime || "",
      // A blog created before the Content DOCX upload existed still holds
      // the old {intro, sections} object shape -- treated as empty here
      // (rather than crashing on render) since it needs a document
      // uploaded again to move to the new content model.
      content: typeof blogEdit?.content === "string" ? blogEdit.content : "",
      faqs: normalizeFaqs(blogEdit?.faqs),
      published:
        blogEdit?.published !== undefined ? String(blogEdit.published) : "true",
    },
    validationSchema: BlogValidationSchema,
    enableReinitialize: true,

    onSubmit: async (values, { resetForm }) => {
      // Save is disabled while the category no longer exists (see
      // isCategoryMissing below); this also covers submitting with Enter.
      if (isCategoryMissing) return;
      const slug = blogEdit?.id ? blogEdit.slug : slugify(values.title);
      const payload = {
        slug,
        title: values.title.trim(),
        category: values.category.trim(),
        description: values.description?.trim() || null,
        readTime: values.readTime?.trim() || null,
        content: values.content,
        faqs: values.faqs.map((g) => ({
          topic: g.topic ? g.topic.trim() : null,
          items: g.items.map((f) => ({ q: f.q.trim(), a: f.a.trim() })),
        })),
        published: values.published === "true",
      };
      try {
        let response;
        if (blogEdit?.id) {
          response = await api.put(
            `/api/admin/management/blogs/${blogEdit.id}`,
            payload,
          );
        } else {
          response = await api.post("/api/admin/management/blogs", payload);
        }
        toast.success(`Blog ${blogEdit?.id ? "updated" : "added"} successfully!`, {
          position: "top-right",
          autoClose: 2000,
        });
        // BlogManagementController returns the BlogResponseDTO directly
        // (not wrapped in the {data:...} ResponseDTO envelope used by
        // Publications/News/FAQ), so the created/updated row is response.data.
        onSuccess(response.data);
        resetForm();
        onClose();
      } catch (error) {
        // Show the backend's own reason when it sends one (e.g. an unknown
        // category or a duplicate slug), same as BlogCategoryModal does --
        // the generic text alone gave no way to tell why a save failed.
        toast.error(
          error.response?.data?.errorMessage || "Failed to submit. Please try again.",
          { autoClose: 6000 },
        );
        console.error("API Error:", error);
      }
    },
  });

  const faqGroups = formik.values.faqs;
  const totalFaqItems = faqGroups.reduce((n, g) => n + g.items.length, 0);

  // FAQs are edited in a small popup (not inline) so the main form stays a
  // fixed height instead of growing with every item added. `faqDraftIndex`
  // addresses an item as { groupIndex, itemIndex } (editing) or null
  // (adding). Manual additions stay scoped to the existing flat-entry
  // workflow -- no topic field there -- always appended to the last group
  // (creating one untitled group first if none exists yet). Editing also
  // shows the item's Topic: a topic belongs to its group, so saving a
  // changed topic renames that one group (every question under it), never
  // any other group.
  const [faqDialogOpen, setFaqDialogOpen] = useState(false);
  const [faqDraftIndex, setFaqDraftIndex] = useState(null);
  const [faqDraft, setFaqDraft] = useState(emptyFaq());
  const [faqDraftTopic, setFaqDraftTopic] = useState("");
  const isEditingFaq = faqDraftIndex !== null;
  const editingGroupSize = isEditingFaq
    ? faqGroups[faqDraftIndex.groupIndex]?.items.length || 0
    : 0;

  // Content DOCX upload -- converts to HTML server-side (docx4j) and stores
  // the result directly in formik's `content` field; the file itself is
  // never kept in form state, only the HTML it produced.
  const contentFileInputRef = useRef(null);
  const [contentUploading, setContentUploading] = useState(false);
  const [contentFileName, setContentFileName] = useState(null);
  const [contentPreviewOpen, setContentPreviewOpen] = useState(false);

  // FAQ preview -- toggles visibility of the existing editable FAQ cards
  // themselves (no separate read-only copy, no "Manage FAQs").
  const [faqPreviewOpen, setFaqPreviewOpen] = useState(false);

  // This component instance stays mounted across opens/closes (only the
  // Modal's `open` prop toggles), so plain useState's initial value only
  // ever applies once -- reset the transient preview toggle explicitly
  // every time the modal opens, so a previous session's "Hide preview"
  // state never leaks into a fresh open.
  useEffect(() => {
    if (open) {
      setFaqPreviewOpen(false);
    }
  }, [open]);

  // Blog categories now come from the admin-managed Blog Categories list
  // (see BlogCategories.jsx) instead of a hardcoded array. Re-fetched every
  // time the modal opens -- this component stays mounted between opens, so
  // a once-per-mount fetch went stale after a category was added or renamed
  // in Manage Categories -- and again whenever `categoriesVersion` changes
  // (Blogs bumps it when Manage Categories closes) in case it's already open.
  // Until the fresh list arrives the missing-category check is suspended
  // (categoriesLoaded), so the previous list never flashes a wrong warning;
  // a response from an earlier, superseded fetch is ignored.
  const [categoriesList, setCategoriesList] = useState([]);
  const [categoriesLoaded, setCategoriesLoaded] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setCategoriesLoaded(false);
    api
      .get("/api/admin/management/blog-categories")
      .then((res) => {
        if (cancelled) return;
        setCategoriesList(res.data || []);
        setCategoriesLoaded(true);
      })
      .catch((err) => console.error("Failed to load blog categories:", err));
    return () => {
      cancelled = true;
    };
  }, [open, categoriesVersion]);

  // A blog can still hold a category that no longer exists in Blog
  // Categories (e.g. "PYQs" from before it was removed there). The backend
  // rejects saving it, so the form flags it and blocks Save until an
  // existing category is chosen -- the blog's data is never changed
  // automatically. Only judged once the list has actually loaded, so a
  // failed/slow fetch never blocks a valid blog (the backend still checks).
  const isCategoryMissing =
    categoriesLoaded &&
    Boolean(formik.values.category) &&
    !categoriesList.some((c) => c.name === formik.values.category);

  // The blog's current category is always listed so the Select shows it
  // (also while the list is still loading); once the loaded list confirms
  // it no longer exists, it's marked and disabled so it can't be picked again.
  const categoryOptions =
    formik.values.category && !categoriesList.some((c) => c.name === formik.values.category)
      ? [
          ...categoriesList,
          { id: `current-${formik.values.category}`, name: formik.values.category, missing: isCategoryMissing },
        ]
      : categoriesList;

  const handleContentDocxChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    const title = formik.values.title.trim();
    if (!title) {
      toast.error("Enter a Title before uploading the Content document.", {
        autoClose: 3000,
      });
      return;
    }
    const slug = blogEdit?.id ? blogEdit.slug : slugify(title);

    setContentUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("slug", slug);
      const response = await api.post(
        "/api/admin/management/blogs/content-docx",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      formik.setFieldValue("content", response.data.content);
      formik.setFieldTouched("content", true, false);
      setContentFileName(file.name);
      toast.success("Content document converted successfully!", {
        autoClose: 2000,
      });
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          "Failed to convert the Content document. Please try again.",
        { autoClose: 3000 },
      );
      console.error("Content DOCX upload error:", error);
    } finally {
      setContentUploading(false);
    }
  };

  // Clears only the current form state (content string + displayed
  // filename/preview) -- nothing is deleted on the backend until the admin
  // clicks Save, same as every other formik field on this form.
  const handleClearContent = () => {
    formik.setFieldValue("content", "");
    setContentFileName(null);
    setContentPreviewOpen(false);
  };

  // FAQ DOCX upload -- parses server-side into [{topic, items:[{q,a}]}] and
  // replaces formik's `faqs` array with the result, same "upload replaces
  // the field" semantics as the Content document upload above. The
  // existing manual Add/Edit/Delete FAQ dialog below still edits this same
  // array (always against the last group -- see openAddFaq).
  const faqFileInputRef = useRef(null);
  const [faqUploading, setFaqUploading] = useState(false);

  const handleFaqDocxChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    setFaqUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await api.post(
        "/api/admin/management/blogs/faqs-docx",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      const parsedGroups = response.data.faqs;
      const parsedItemCount = (parsedGroups || []).reduce(
        (n, g) => n + (g.items?.length || 0),
        0,
      );
      if (!parsedGroups || parsedItemCount === 0) {
        // A parse that finds zero Q&A pairs isn't a successful upload --
        // leave the admin's existing faqs (if any) untouched rather than
        // wiping them with an empty array.
        toast.error(
          "No FAQ questions found in this document. Please upload a DOCX containing questions in the format Q1., Q2., etc.",
          { autoClose: 4000 },
        );
      } else {
        formik.setFieldValue("faqs", parsedGroups);
        toast.success(
          `FAQ document parsed successfully (${parsedItemCount} question${parsedItemCount === 1 ? "" : "s"})!`,
          { autoClose: 2500 },
        );
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          "Failed to parse the FAQ document. Please try again.",
        { autoClose: 3000 },
      );
      console.error("FAQ DOCX upload error:", error);
    } finally {
      setFaqUploading(false);
    }
  };

  // Clears only the current form state (faqs array) -- nothing is deleted
  // on the backend until the admin clicks Save, same as handleClearContent.
  const handleClearFaqs = () => {
    formik.setFieldValue("faqs", []);
    setFaqPreviewOpen(false);
  };

  // Closing without saving (Cancel, backdrop click, Esc) discards unsaved
  // edits. This component stays mounted between opens and Formik only
  // re-initializes when `blogEdit` changes, so without this reset reopening
  // the same blog showed the abandoned edits as if they had been saved
  // (nothing is ever sent to the backend except from Save/submit).
  const handleClose = () => {
    formik.resetForm();
    setContentFileName(null);
    onClose();
  };

  const openAddFaq = () => {
    setFaqDraftIndex(null);
    setFaqDraft(emptyFaq());
    setFaqDialogOpen(true);
  };

  const openEditFaq = (groupIndex, itemIndex) => {
    setFaqDraftIndex({ groupIndex, itemIndex });
    setFaqDraft(faqGroups[groupIndex].items[itemIndex]);
    setFaqDraftTopic(faqGroups[groupIndex].topic || "");
    setFaqDialogOpen(true);
  };

  const saveFaqDraft = () => {
    if (!faqDraft.q.trim() || !faqDraft.a.trim()) return;
    if (isEditingFaq && !faqDraftTopic.trim()) return;
    if (faqDraftIndex === null) {
      // Adding: always appended to the last group, creating one untitled
      // group first if none exists yet -- manual additions have no topic
      // UI of their own.
      const groups = faqGroups.length > 0 ? faqGroups : [{ topic: null, items: [] }];
      const lastIndex = groups.length - 1;
      formik.setFieldValue(
        "faqs",
        groups.map((g, i) => (i === lastIndex ? { ...g, items: [...g.items, faqDraft] } : g)),
      );
    } else {
      const { groupIndex, itemIndex } = faqDraftIndex;
      formik.setFieldValue(
        "faqs",
        faqGroups.map((g, gi) =>
          gi === groupIndex
            ? {
                ...g,
                topic: faqDraftTopic.trim(),
                items: g.items.map((f, fi) => (fi === itemIndex ? faqDraft : f)),
              }
            : g,
        ),
      );
    }
    setFaqDialogOpen(false);
  };

  const removeFaq = (groupIndex, itemIndex) => {
    formik.setFieldValue(
      "faqs",
      faqGroups
        .map((g, gi) => (gi === groupIndex ? { ...g, items: g.items.filter((_, fi) => fi !== itemIndex) } : g))
        // Drop a group left with zero items so an empty topic heading
        // never shows with nothing under it.
        .filter((g) => g.items.length > 0),
    );
  };

  return (
    <>
    <Modal open={open} onClose={handleClose} disableScrollLock>
      <Box
        sx={{ ...MODAL_STYLES.large, width: { xs: 340, md: 700 } }}
        component="form"
        onSubmit={formik.handleSubmit}
      >
        <Typography variant="h5" component="h2" sx={{ mb: 3, fontWeight: 600 }}>
          {blogEdit && Object.keys(blogEdit).length > 0 ? "Update Blog" : "Add Blog"}
        </Typography>

        <Box sx={FORM_STYLES.container}>
          <TextField
            label="Title"
            name="title"
            value={formik.values.title}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.title && Boolean(formik.errors.title)}
            helperText={formik.touched.title && formik.errors.title}
            {...TEXT_FIELD_STYLES.standard}
            required
          />

          <Box sx={FORM_STYLES.row}>
            <FormControl
              {...TEXT_FIELD_STYLES.standard}
              sx={{ ...TEXT_FIELD_STYLES.standard.sx, ...FORM_STYLES.field.sx }}
              error={isCategoryMissing || (formik.touched.category && Boolean(formik.errors.category))}
              required
            >
              <InputLabel>Category</InputLabel>
              <Select
                label="Category"
                name="category"
                value={formik.values.category}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              >
                {categoryOptions.map((category) => (
                  <MenuItem key={category.id} value={category.name} disabled={category.missing}>
                    {category.missing ? `${category.name} (no longer exists)` : category.name}
                  </MenuItem>
                ))}
              </Select>
              {isCategoryMissing ? (
                <FormHelperText>
                  {`"${formik.values.category}" no longer exists in Blog Categories. Choose an existing category to save this blog.`}
                </FormHelperText>
              ) : (
                formik.touched.category &&
                formik.errors.category && <FormHelperText>{formik.errors.category}</FormHelperText>
              )}
            </FormControl>

            <TextField
              label="Read Time"
              name="readTime"
              placeholder="e.g. 6 min read"
              value={formik.values.readTime}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.readTime && Boolean(formik.errors.readTime)}
              helperText={formik.touched.readTime && formik.errors.readTime}
              {...TEXT_FIELD_STYLES.standard}
              sx={{ ...TEXT_FIELD_STYLES.standard.sx, ...FORM_STYLES.field.sx }}
            />
          </Box>

          <TextField
            multiline
            minRows={2}
            label="Description"
            name="description"
            value={formik.values.description}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.description && Boolean(formik.errors.description)}
            helperText={formik.touched.description && formik.errors.description}
            {...TEXT_FIELD_STYLES.standard}
          />

          <FormControl {...TEXT_FIELD_STYLES.standard}>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              name="published"
              value={formik.values.published}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.published && Boolean(formik.errors.published)}
            >
              <MenuItem value="true">Active</MenuItem>
              <MenuItem value="false">Inactive</MenuItem>
            </Select>
          </FormControl>

          <Divider sx={{ mt: 1 }} />

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Content
            </Typography>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}
            >
              <Button
                size="small"
                startIcon={<UploadFileIcon />}
                disabled={contentUploading}
                onClick={() => contentFileInputRef.current?.click()}
                {...BUTTON_STYLES.small}
              >
                {contentUploading
                  ? "Converting…"
                  : formik.values.content
                    ? "Replace Content Document"
                    : "Upload Content Document (.docx)"}
              </Button>
              <input
                ref={contentFileInputRef}
                type="file"
                accept=".docx"
                hidden
                onChange={handleContentDocxChange}
              />
              {formik.values.content && (
                <>
                  <Typography variant="caption" color="text.secondary">
                    {contentFileName || "Existing content loaded"}
                  </Typography>
                  <Button
                    size="small"
                    color="error"
                    onClick={handleClearContent}
                    {...BUTTON_STYLES.small}
                  >
                    Remove Content Document
                  </Button>
                </>
              )}
            </Box>
          </Box>

          {formik.touched.content && formik.errors.content && (
            <Typography variant="caption" color="error" sx={{ mt: -1 }}>
              {formik.errors.content}
            </Typography>
          )}

          {formik.values.content && (
            <>
              <Button
                size="small"
                onClick={() => setContentPreviewOpen((v) => !v)}
                sx={{ alignSelf: "flex-start", textTransform: "none" }}
              >
                {contentPreviewOpen ? "Hide preview" : "Show preview"}
              </Button>
              {contentPreviewOpen && (
                <Box
                  sx={{
                    p: 2,
                    maxHeight: 300,
                    overflow: "auto",
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                  }}
                  // Admin-only authenticated preview of HTML this same
                  // admin just converted from their own uploaded document
                  // -- not public-facing output, so no sanitizer here (the
                  // website applies its own SafeHtml sanitizer when it
                  // actually renders this content to visitors).
                  dangerouslySetInnerHTML={{ __html: formik.values.content }}
                />
              )}
            </>
          )}

          <Divider sx={{ mt: 1 }} />

          <Box
            sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              FAQs
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                startIcon={<UploadFileIcon />}
                disabled={faqUploading}
                onClick={() => faqFileInputRef.current?.click()}
                {...BUTTON_STYLES.small}
              >
                {faqUploading
                  ? "Parsing…"
                  : totalFaqItems > 0
                    ? "Replace FAQ Document"
                    : "Upload FAQ Document (.docx)"}
              </Button>
              <input
                ref={faqFileInputRef}
                type="file"
                accept=".docx"
                hidden
                onChange={handleFaqDocxChange}
              />
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={openAddFaq}
                {...BUTTON_STYLES.small}
              >
                Add FAQ
              </Button>
              {totalFaqItems > 0 && (
                <Button
                  size="small"
                  color="error"
                  onClick={handleClearFaqs}
                  {...BUTTON_STYLES.small}
                >
                  Remove FAQ Document
                </Button>
              )}
            </Stack>
          </Box>

          {totalFaqItems > 0 && (
            <Button
              size="small"
              onClick={() => setFaqPreviewOpen((v) => !v)}
              sx={{ alignSelf: "flex-start", textTransform: "none" }}
            >
              {faqPreviewOpen ? "Hide preview" : "Show preview"}
            </Button>
          )}

          {totalFaqItems === 0 && (
            <Typography variant="body2" color="text.secondary">
              No FAQs added.
            </Typography>
          )}

          {faqPreviewOpen &&
            faqGroups.map((group, groupIndex) => (
              <Box
                key={groupIndex}
                sx={{ display: "flex", flexDirection: "column", gap: 1 }}
              >
                {group.topic && (
                  <Typography variant="body2" sx={{ fontWeight: 700, color: "#17479b" }}>
                    {group.topic}
                  </Typography>
                )}
                {group.items.map((faq, itemIndex) => (
                  <Box
                    key={itemIndex}
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 2,
                      p: 1.5,
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      gap: 1,
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {faq.q || `FAQ ${itemIndex + 1}`}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {faq.a}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                      <IconButton
                        size="small"
                        onClick={() => openEditFaq(groupIndex, itemIndex)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => removeFaq(groupIndex, itemIndex)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Box>
                ))}
              </Box>
            ))}
        </Box>

        <Box
          sx={FORM_STYLES.actions}
          style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}
        >
          <Button onClick={handleClose} {...BUTTON_STYLES.secondary}>
            Cancel
          </Button>
          <Button type="submit" disabled={isCategoryMissing} {...BUTTON_STYLES.primary}>
            Save
          </Button>
        </Box>
      </Box>
    </Modal>

      <Dialog
        open={faqDialogOpen}
        onClose={() => setFaqDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{faqDraftIndex === null ? "Add FAQ" : "Edit FAQ"}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, pt: 1 }}>
            {isEditingFaq && (
              <TextField
                autoFocus
                label="Topic"
                value={faqDraftTopic}
                onChange={(e) => setFaqDraftTopic(e.target.value)}
                helperText={
                  editingGroupSize > 1
                    ? `Changing the topic renames it for all ${editingGroupSize} questions in this topic.`
                    : undefined
                }
                {...TEXT_FIELD_STYLES.standard}
                required
              />
            )}
            <TextField
              autoFocus={!isEditingFaq}
              label="Question"
              value={faqDraft.q}
              onChange={(e) => setFaqDraft((d) => ({ ...d, q: e.target.value }))}
              {...TEXT_FIELD_STYLES.standard}
              required
            />
            <TextField
              multiline
              minRows={3}
              label="Answer"
              value={faqDraft.a}
              onChange={(e) => setFaqDraft((d) => ({ ...d, a: e.target.value }))}
              {...TEXT_FIELD_STYLES.standard}
              required
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setFaqDialogOpen(false)} {...BUTTON_STYLES.secondary}>
            Cancel
          </Button>
          <Button
            onClick={saveFaqDraft}
            disabled={
              !faqDraft.q.trim() ||
              !faqDraft.a.trim() ||
              (isEditingFaq && !faqDraftTopic.trim())
            }
            {...BUTTON_STYLES.primary}
          >
            {faqDraftIndex === null ? "Add FAQ" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default BlogModal;
